import { CheckoutTotals, OrderItem, VoucherResult } from '../model/schemas/checkout';

// Constants for fee calculations (should match Flutter app)
const COIN_RATE = 100; // 1 coin per Rs 100 spent

// Legacy constants for backward compatibility (deprecated - use dynamic shipping service)
export const SHIPPING_FLAT = 165; // Fixed shipping fee (165 NPR) - DEPRECATED

/**
 * Calculate preview totals (legacy function)
 * SECURITY: Input validation and DoS prevention
 */
export function calcPreviewTotals(subtotal: number) {
  // SECURITY: Validate input
  if (!Number.isFinite(subtotal) || isNaN(subtotal)) {
    return { shipping: SHIPPING_FLAT, total: SHIPPING_FLAT };
  }
  
  // SECURITY: Sanitize subtotal to prevent DoS
  const sanitizedSubtotal = sanitizeNumber(subtotal, 0, MAX_SUBTOTAL, 0);
  const shipping = SHIPPING_FLAT;
  const total = sanitizedSubtotal + shipping;
  
  // SECURITY: Ensure total is finite
  const finalTotal = Number.isFinite(total) ? total : shipping;
  
  return { shipping, total: Math.round(finalTotal) };
}

export interface CalcTotalsParams {
  items: OrderItem[];
  shippingFee: number;
  voucherResult?: VoucherResult;
  coinsToRedeem?: number;
  userCoinsBalance?: number;
}

// SECURITY: Maximum values to prevent DoS and overflow
const MAX_ITEM_PRICE = 1000000; // 1 million NPR
const MAX_ITEM_QUANTITY = 1000;
const MAX_SUBTOTAL = 10000000; // 10 million NPR
const MAX_SHIPPING_FEE = 10000; // 10k NPR
const MAX_COINS = 100000; // 100k coins

// SECURITY: Validate and sanitize number
function sanitizeNumber(value: number, min: number, max: number, defaultValue: number): number {
  if (!Number.isFinite(value) || isNaN(value)) return defaultValue;
  return Math.max(min, Math.min(max, value));
}

// SECURITY: Validate order item
function validateOrderItem(item: OrderItem): boolean {
  if (!item || typeof item !== 'object') return false;
  if (!Number.isFinite(item.price) || item.price < 0 || item.price > MAX_ITEM_PRICE) return false;
  if (!Number.isFinite(item.quantity) || item.quantity < 1 || item.quantity > MAX_ITEM_QUANTITY) return false;
  return true;
}

/**
 * Calculate checkout totals with all fees and discounts
 * Parity with Flutter app fee calculations
 * SECURITY: Input validation and DoS prevention
 */
export function calcTotals({
  items,
  shippingFee,
  voucherResult,
  coinsToRedeem = 0,
  userCoinsBalance = 0
}: CalcTotalsParams): CheckoutTotals {
  // SECURITY: Validate inputs
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items array is required and must not be empty');
  }

  // SECURITY: Validate and filter invalid items
  const validItems = items.filter(validateOrderItem);
  if (validItems.length === 0) {
    throw new Error('No valid items found');
  }

  // SECURITY: Sanitize shipping fee
  const sanitizedShippingFee = sanitizeNumber(shippingFee, 0, MAX_SHIPPING_FEE, 0);

  // SECURITY: Sanitize coin values
  const sanitizedCoinsToRedeem = sanitizeNumber(coinsToRedeem, 0, MAX_COINS, 0);
  const sanitizedUserCoinsBalance = sanitizeNumber(userCoinsBalance, 0, MAX_COINS, 0);

  // Calculate subtotal with validated items
  const subtotal = validItems.reduce((sum, item) => {
    const price = sanitizeNumber(item.price, 0, MAX_ITEM_PRICE, 0);
    const quantity = sanitizeNumber(item.quantity, 1, MAX_ITEM_QUANTITY, 1);
    return sum + (price * quantity);
  }, 0);

  // SECURITY: Cap subtotal to prevent overflow
  const cappedSubtotal = Math.min(subtotal, MAX_SUBTOTAL);
  
  // Calculate discount from voucher
  let discount = 0;
  if (voucherResult) {
    // SECURITY: Validate voucher result
    if (voucherResult.type === 'percent') {
      const percentValue = sanitizeNumber(voucherResult.value, 0, 100, 0); // Max 100%
      discount = (cappedSubtotal * percentValue) / 100;
    } else {
      discount = sanitizeNumber(voucherResult.value, 0, cappedSubtotal, 0);
    }
    // Ensure discount doesn't exceed subtotal
    discount = Math.min(discount, cappedSubtotal);
  }
  
  // Calculate coins redeemed (capped by balance and subtotal)
  const maxCoinsRedeemable = Math.min(sanitizedUserCoinsBalance, cappedSubtotal);
  const actualCoinsRedeemed = Math.min(sanitizedCoinsToRedeem, maxCoinsRedeemable);
  
  // Calculate amount after discounts
  const amountAfterDiscounts = Math.max(0, cappedSubtotal - discount - actualCoinsRedeemed);
  
  // Calculate total (no additional fees)
  const total = amountAfterDiscounts + sanitizedShippingFee;
  
  // SECURITY: Ensure total is finite and positive
  const finalTotal = Number.isFinite(total) && total >= 0 ? total : 0;
  
  // Calculate coins earned (based on final total)
  const coinsEarned = Math.floor(finalTotal / COIN_RATE);
  
  return {
    subtotal: cappedSubtotal,
    discount,
    coinsRedeemed: actualCoinsRedeemed,
    shippingFee: sanitizedShippingFee,
    total: Math.round(finalTotal),
    coinsEarned: Math.max(0, coinsEarned), // Ensure non-negative
    itemsCount: validItems.reduce((sum, item) => sum + sanitizeNumber(item.quantity, 1, MAX_ITEM_QUANTITY, 1), 0)
  };
}

/**
 * Get fee breakdown for tooltip display
 */
export function getFeeBreakdown(totals: CheckoutTotals) {
  return [
    { label: 'Subtotal', amount: totals.subtotal },
    ...(totals.discount > 0 ? [{ label: 'Discount', amount: -totals.discount }] : []),
    ...(totals.coinsRedeemed > 0 ? [{ label: 'Coins Redeemed', amount: -totals.coinsRedeemed }] : []),
    { label: 'Shipping Fee', amount: totals.shippingFee },
    { label: 'Total', amount: totals.total, isTotal: true }
  ];
}

/**
 * Format currency in NPR format
 */
export function formatCurrencyNPR(amount: number): string {
  return new Intl.NumberFormat('en-NP', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Calculate percentage discount
 * SECURITY: Input validation and division by zero prevention
 */
export function calculatePercentageDiscount(originalPrice: number, discountedPrice: number): number {
  // SECURITY: Validate inputs
  if (!Number.isFinite(originalPrice) || !Number.isFinite(discountedPrice)) {
    return 0;
  }
  
  // SECURITY: Prevent division by zero
  if (originalPrice <= 0) return 0;
  
  // SECURITY: Sanitize values
  const sanitizedOriginal = sanitizeNumber(originalPrice, 0, MAX_ITEM_PRICE, 0);
  const sanitizedDiscounted = sanitizeNumber(discountedPrice, 0, MAX_ITEM_PRICE, 0);
  
  // SECURITY: Ensure discounted price doesn't exceed original
  const validDiscounted = Math.min(sanitizedDiscounted, sanitizedOriginal);
  
  const percentage = ((sanitizedOriginal - validDiscounted) / sanitizedOriginal) * 100;
  
  // SECURITY: Ensure result is finite and within valid range
  return Number.isFinite(percentage) ? Math.round(Math.max(0, Math.min(100, percentage))) : 0;
}

// SECURITY: Maximum weight to prevent DoS
const MAX_WEIGHT_KG = 1000; // 1000kg max
const MAX_BASE_FEE = 10000; // 10k NPR max
const MAX_PER_HALF_KG = 1000; // 1k NPR max

/**
 * Convert weight to kilograms from various units
 * Used for delivery calculations - ensures all weights are in kg
 * 
 * @param weight - The weight value
 * @param unit - The unit of the weight (kg, g, lbs, lb, pound, pounds, gram, grams, oz, ounce, ounces)
 * @returns Weight in kilograms
 */
export function convertWeightToKilograms(weight: number, unit?: string): number {
  if (!Number.isFinite(weight) || weight <= 0) {
    return 0;
  }

  // If no unit specified or unit is already kg, return as-is
  if (!unit || unit.toLowerCase().trim() === '') {
    // Default to kg if no unit specified (backward compatibility)
    return weight;
  }

  const normalizedUnit = unit.toLowerCase().trim();

  // Convert to kg based on unit
  switch (normalizedUnit) {
    case 'kg':
    case 'kilogram':
    case 'kilograms':
      return weight;

    case 'g':
    case 'gram':
    case 'grams':
      return weight / 1000; // Convert grams to kg

    case 'lbs':
    case 'lb':
    case 'pound':
    case 'pounds':
      return weight * 0.453592; // Convert pounds to kg (1 lb = 0.453592 kg)

    case 'oz':
    case 'ounce':
    case 'ounces':
      return weight * 0.0283495; // Convert ounces to kg (1 oz = 0.0283495 kg)

    default:
      // Unknown unit - assume kg (backward compatibility)
      // Note: In production, you might want to log this warning
      return weight;
  }
}

/**
 * PRIMARY weight-based shipping fee calculation function.
 * This is the main shipping calculation logic used throughout the application.
 * Server-side code in functions/src/orders.ts mirrors this logic.
 * 
 * Pricing: Rs 165 for up to 2kg, Rs 40 for each additional 0.5kg after 2kg
 * 
 * @param totalWeightKg - Total weight in kilograms (sum of item.weight * quantity)
 * @param baseFee - Base shipping fee (default 165 NPR)
 * @param perHalfKg - Additional fee per 0.5kg after 2kg (default 40 NPR)
 * @returns Shipping fee in NPR
 * SECURITY: Input validation and DoS prevention
 */
export function calcWeightBasedShippingClient(
  totalWeightKg: number,
  baseFee = 165,
  perHalfKg = 40
): number {
  // SECURITY: Validate inputs
  if (!Number.isFinite(totalWeightKg) || totalWeightKg <= 0) {
    return sanitizeNumber(baseFee, 0, MAX_BASE_FEE, 165);
  }

  // SECURITY: Sanitize weight (prevent DoS via extremely large weights)
  const sanitizedWeight = Math.min(totalWeightKg, MAX_WEIGHT_KG);
  const sanitizedBaseFee = sanitizeNumber(baseFee, 0, MAX_BASE_FEE, 165);
  const sanitizedPerHalfKg = sanitizeNumber(perHalfKg, 0, MAX_PER_HALF_KG, 40);

  if (sanitizedWeight <= 2.0) return sanitizedBaseFee; // Rs 165 for up to 2kg

  // Rs 40 for each additional 0.5kg after 2kg
  const extraHalfSteps = Math.ceil((sanitizedWeight - 2.0) / 0.5);
  const calculatedFee = sanitizedBaseFee + (extraHalfSteps * sanitizedPerHalfKg);
  
  // SECURITY: Cap total fee to prevent overflow
  return Math.min(calculatedFee, MAX_SHIPPING_FEE);
}